import { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  useTheme,
} from "@mui/material";
import { ChromePicker } from "react-color";
import {
  EditOutlined,
  ColorLensOutlined,
  ImageOutlined,
  CloudUpload,
} from "@mui/icons-material";
import { StandardButton } from "../../components/StandardButton";
import { StandardTextInput } from "../../components/StandardTextInput";
import { Tables } from "../../../database.types";
import { App } from "../../types/types";

interface EditTeamDialogProps {
  open: boolean;
  team: App.TeamWithMembers;
  logoPreview: string | null;
  onClose: () => void;
  onSave: (team: App.TeamWithMembers, logoFile: File | null) => void;
}

export const EditTeamDialog = ({
  open,
  team: initialTeam,
  logoPreview: initialLogoPreview,
  onClose,
  onSave,
}: EditTeamDialogProps) => {
  const theme = useTheme();
  const [team, setTeam] = useState<App.TeamWithMembers>(initialTeam);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    initialLogoPreview,
  );

  const handleTeamChange = (field: keyof Tables<"teams">, value: string) => {
    setTeam((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setLogoFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    setTeam(initialTeam);
    setLogoFile(null);
    setLogoPreview(initialLogoPreview);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
    >
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          borderRadius: 2,
          boxShadow: 2,
          backgroundColor: theme.palette.primary.dark,
          border: `1px solid ${theme.palette.primary.main}`,
        }}
      >
        <CardHeader
          title={
            <Box display="flex" width="100%" px={2}>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                  color: "white",
                }}
              >
                <EditOutlined />
                Edit Team
              </Typography>
            </Box>
          }
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.primary.dark,
            p: { xs: 1, sm: 2 },
          }}
        />
        <CardContent
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Team Name */}
          <StandardTextInput
            label="Team Name"
            value={team.name}
            onChange={(e) => handleTeamChange("name", e.target.value)}
            fullWidth
            autoFocus
          />

          {/* Team Color */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: 600,
              }}
            >
              <ColorLensOutlined fontSize="small" />
              Team Color
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <ChromePicker
                color={team.color}
                onChange={(color: any) => handleTeamChange("color", color.hex)}
                disableAlpha
                styles={{
                  default: {
                    picker: {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  },
                }}
              />
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  backgroundColor: team.color,
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                  flexShrink: 0,
                }}
              />
            </Box>
          </Paper>

          {/* Team Logo */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: 600,
              }}
            >
              <ImageOutlined fontSize="small" />
              Team Logo
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {logoPreview && (
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo preview"
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: "contain",
                    borderRadius: 2,
                    border: "2px solid rgba(255, 255, 255, 0.2)",
                  }}
                />
              )}
              <StandardButton
                component="label"
                variant="outlined"
                color="primary"
                startIcon={<CloudUpload />}
                size="medium"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.6)",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                  },
                }}
              >
                {logoPreview ? "Change Logo" : "Upload Logo"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleLogoChange}
                />
              </StandardButton>
            </Box>
          </Paper>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              pt: 1,
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <StandardButton
              variant="outlined"
              size="medium"
              onClick={handleCancel}
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                borderColor: "rgba(255, 255, 255, 0.3)",
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.6)",
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
            >
              Cancel
            </StandardButton>
            <StandardButton
              variant="contained"
              size="medium"
              onClick={() => onSave(team, logoFile)}
            >
              Save
            </StandardButton>
          </Box>
        </CardContent>
      </Card>
    </Dialog>
  );
};
